import React, { useState, useEffect, useRef, useCallback } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import "./App.scss";
import "../styles/sharedStyles.scss";
import Nsc from "./pure/Nsc";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import Footer from "../shared/Footer/Footer";
import QuickStart from "../components/quickStart/QuickStart";
import ProgressBarTabs from "../components/progressBar/ProgressBarTabs";
import InvalidRoute from "../components/accessDenied/InvalidRoute";
import Reporting from "../components/reporting/Reporting";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import PricingWrapper from "../components/pricing/pricingWrapper/PricingWrapper";
import AccessDeniedNotOnboardedNsc from "../components/accessDenied/AccessDeniedNotOnboardedNsc";
import HasPermissionTo from "../common/HasPermissionTo";
import NscDashboard from "../components/dashboard/nscDashBoard/NscDashboard";
import DepositPricing from "../components/pricing/pricingWrapper/depositPricing/DepositPricing";
import NscOnboardingClient from "../shared/clients/NscOnboardingClient";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import ErrorScreen from "../errors/ErrorScreen";
import { IS_SA_MARKET } from "../utils/EmpUtil";
import OrdersSearch from "../components/search/searchWrapper/orders/OrdersSearch";
import SearchWrapper from "../components/search/searchWrapper/SearchWrapper";

function NSCApp(props) {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] = useState(false);
  const [padding, setPaddingState] = useState("70px");

  const userRef = useRef(null);
  const localizedInfoRef = useRef({});
  const onBoardingStatusRef = useRef(null);

  if (!userRef.current) {
    userRef.current = new Nsc(props.userDetails, props.history);
  }

  const user = userRef.current;
  const localizedInfo = localizedInfoRef.current;

  const onReservationSave = useCallback(() => {
    onBoardingStatusRef.current = "ACCEPTED";
    navigateToRoute("dashboard");
  }, []);

  function navigateToRoute(link) {
    props.history.push(`/${link}`);
  }

  const isCurrentPathAllowedToShowHeaderAndFooter = useCallback((currentPathName) => {
    const staticAllowedPaths = [
      "/dashboard",
      "/pricing",
      "/pricing/vehicles",
      "/pricing/accessories",
      "/pricing/deposit",
      "/business-settings",
      "/search/orders",
      "/search",
      "/reporting",
      "/",
    ];

    return staticAllowedPaths.includes(currentPathName);
  }, []);

  const setNavigationRestriction = useCallback(() => {
    if (onBoardingStatusRef.current !== "ACCEPTED") {
      setShouldNavigationBeRestricted(true);
    } else {
      const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
        props.history.location.pathname
      );
      setShouldNavigationBeRestricted(!isAllowedPath);
    }
  }, [props.history.location.pathname, isCurrentPathAllowedToShowHeaderAndFooter]);

  function setPadding(padding) {
    if (padding > 70) {
      setPaddingState(padding + "px");
    }
  }

  async function isOnboarded() {
    return await NscOnboardingClient.isOnboarded(user);
  }

  function checkIsUserOnboarded() {
    isOnboarded()
      .then((val) => {
        onBoardingStatusRef.current = val.onboarded ? "ACCEPTED" : "PENDING";
        setNavigationRestriction();
      })
      .finally(() => {
        user.hasNscCompletedBasicPAs = true;
        setRenderApp(true);
      });
  }

  function getLocalizedInfo() {
    props.hideOrShow(true);
    LocalizedInfoClient.getLocalizedInfo(user)
      .then((response) => {
        localizedInfoRef.current = Object.assign(
          {},
          { ...localizedInfoRef.current },
          { ...response }
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        props.i18n.changeLanguage("en" + "_" + "AUS");
        checkIsUserOnboarded();
        props.hideOrShow(false);
      });
  }

  function getProgressBarTabs() {
    return IS_SA_MARKET(user.market)
      ? ProgressBarTabs().onboardingSANsc
      : ProgressBarTabs().onboardingNsc;
  }

  useEffect(() => {
    getLocalizedInfo();
  }, []);

  useEffect(() => {
    const unlisten = props.history.listen(() => setNavigationRestriction());
    return () => {
      unlisten();
    };
  }, [props.history, setNavigationRestriction]);

  const ProtectedRoute = ({
                            component: Component,
                            perform: perform,
                            ...rest
                          }) => (
    <HasPermissionTo
      perform={perform}
      permissions={user.permissions.rolePermissions}
      render={() => (
        <Route
          {...rest}
          render={() =>
            onBoardingStatusRef.current === "ACCEPTED" ? (
              <Component {...rest.customprops} {...props} />
            ) : (
              <Redirect to="/" />
            )
          }
        />
      )}
    />
  );

  return (
    renderApp && (
      <Router history={props.history}>
        <div className="appWrapper" id="appWrapper">
          {!shouldNavigationBeRestricted && (
            <div style={{ paddingTop: padding }}>
              <GlobalNavBar
                {...props}
                user={user}
                urlPath={window.location.pathname}
                navigateToRoute={navigateToRoute}
                setPadding={setPadding}
              />
            </div>
          )}

          <Switch>
            <ProtectedRoute
              exact
              path="/pricing"
              component={withIdleTimer(PricingWrapper)}
              perform={["pricingPage"]}
              customprops={{
                ...props,
                user: user,
                localizedInfo: localizedInfo,
              }}
            />
            <ProtectedRoute
              exact
              path="/dashboard"
              component={withIdleTimer(NscDashboard)}
              perform={["dashBoardPage"]}
              customprops={{
                user: user,
              }}
            />

            <ProtectedRoute
              exact
              path="/reporting"
              component={withIdleTimer(Reporting)}
              perform={["reportingPage"]}
              customprops={{
                user: user,
              }}
            />
            <ProtectedRoute
              exact
              path="/business-settings"
              component={withIdleTimer(BusinessSettings)}
              perform={["businessSettingsPage"]}
              customprops={{
                user: user,
                localizedInfo: localizedInfo,
              }}
            />
            <ProtectedRoute
              exact
              path="/search"
              component={withIdleTimer(SearchWrapper)}
              perform={["searchLandingPage"]}
              customprops={{
                user: user,
              }}
            />
            <Route
              exact
              path="/search/orders"
              component={withIdleTimer(() => (
                <OrdersSearch
                  {...props}
                  localizedInfo={localizedInfo}
                  user={user}
                />
              ))}
            />
            <ProtectedRoute
              exact
              path="/pricing/deposit"
              component={withIdleTimer(() => (
                <DepositPricing
                  {...props}
                  localizedInfo={localizedInfo}
                  user={user}
                />
              ))}
              perform={["depositPricingTab"]}
            />

            <Route
              exact
              path="/denied-nsc"
              component={AccessDeniedNotOnboardedNsc}
            />
            <Route
              exact
              path="/"
              render={() =>
                onBoardingStatusRef.current === "ACCEPTED" ? (
                  <Redirect
                    to={{
                      pathname: "/dashboard",
                    }}
                  />
                ) : (
                  <HasPermissionTo
                    perform={["quickStartPage"]}
                    permissions={user.permissions.rolePermissions}
                    render={() => (
                      <QuickStart
                        user={user}
                        {...props}
                        onboardingClient={NscOnboardingClient}
                        localizedInfo={localizedInfo}
                        onSave={onReservationSave}
                        progressBarTabs={getProgressBarTabs()}
                        fromQuickstart={true}
                      />
                    )}
                    noRender={() => (
                      <Redirect
                        to={{
                          pathname: "/denied-nsc",
                        }}
                      />
                    )}
                  />
                )
              }
            />
            <Route
              exact
              path="/error"
              render={(routeProps) => <ErrorScreen {...routeProps} />}
            />
            <Route component={withIdleTimer(InvalidRoute)} />
          </Switch>
          {!shouldNavigationBeRestricted && (
            <Footer permissions={props.userDetails.permissions} />
          )}
        </div>
      </Router>
    )
  );
}

export default NSCApp;
